import React, { useRef, useState, useEffect } from 'react';
import { Mail, Linkedin, MapPin, ArrowUpRight } from 'lucide-react';
import { personalInfo, socialLinks } from '../mock';

const iconMap = { Linkedin, Mail };

const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const Contact = () => {
  const [headerRef, headerVisible] = useReveal(0.2);
  const [bodyRef, bodyVisible] = useReveal(0.15);

  return (
    <section
      id="contact"
      className="portfolio-section"
      style={{ paddingTop: '100px', paddingBottom: '100px' }}
    >
      <div className="max-w-6xl mx-auto px-6">

        {/* Header */}
        <div
          ref={headerRef}
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            marginBottom: '52px'
          }}
        >
          <p className="section-label mb-3">Get In Touch</p>
          <h2
            className="font-bold tracking-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--text-primary)', lineHeight: 1.1 }}
          >
            Let's Build Something{' '}
            <span className="accent-text">Together</span>
          </h2>
          <p
            className="mt-2 text-sm"
            style={{ color: 'var(--text-secondary)', maxWidth: '460px', lineHeight: 1.65 }}
          >
            Have a project in mind or want to explore collaboration? Drop me an email — I reply within 24 hours.
          </p>
        </div>

        {/* Body */}
        <div
          ref={bodyRef}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          style={{
            opacity: bodyVisible ? 1 : 0,
            transform: bodyVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s'
          }}
        >
          {/* Left — Big email CTA */}
          <div
            className="glass-card p-8 flex flex-col gap-6"
            style={{ borderLeft: '3px solid var(--accent)' }}
          >
            <div className="flex items-center gap-3">
              <span className="available-dot" />
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Currently Available for Work
              </span>
            </div>

            <div>
              <p className="text-xs mono mb-2" style={{ color: 'var(--text-muted)' }}>
                SEND ME AN EMAIL
              </p>
              <a
                href={`mailto:${personalInfo.email}`}
                className="font-bold flex items-center gap-2 group"
                style={{
                  fontSize: 'clamp(1rem, 2vw, 1.35rem)',
                  color: 'var(--accent)',
                  textDecoration: 'none',
                  lineHeight: 1.2,
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.75'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                {personalInfo.email}
                <ArrowUpRight size={20} style={{ flexShrink: 0 }} />
              </a>
            </div>

            <a
              href={`mailto:${personalInfo.email}`}
              className="btn-accent gap-2.5 px-7 py-3.5 text-sm font-semibold justify-center"
              style={{ textDecoration: 'none', border: 'none', width: 'fit-content' }}
            >
              <Mail size={15} />
              Send Email
            </a>

            <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Open to full-time positions, contract work, and interesting freelance projects.
            </p>
          </div>

          {/* Right — Location + socials */}
          <div className="flex flex-col gap-5">
            {/* Location */}
            <div
              className="glass-card p-5 flex items-center gap-4"
            >
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                background: 'var(--tag-bg)', border: '1px solid var(--tag-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--accent)'
              }}>
                <MapPin size={18} />
              </div>
              <div>
                <p className="text-xs mono mb-0.5" style={{ color: 'var(--text-muted)' }}>Location</p>
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {personalInfo.location}
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  Remote-first · Open to relocation
                </p>
              </div>
            </div>

            {/* Social links */}
            <div className="glass-card p-5">
              <p className="text-xs mono mb-4" style={{ color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Find me on
              </p>
              <div className="flex flex-col gap-3">
                {socialLinks.map((link) => {
                  const Icon = iconMap[link.icon];
                  if (!Icon) return null;
                  return (
                    <a
                      key={link.label}
                      href={link.url}
                      target={link.icon !== 'Mail' ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 group"
                      style={{ textDecoration: 'none', transition: 'opacity 0.2s ease' }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                      <div style={{
                        width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                        background: 'var(--tag-bg)', border: '1px solid var(--tag-border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--accent)'
                      }}>
                        <Icon size={16} />
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          {link.label}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {link.icon === 'Mail' ? personalInfo.email : link.url.replace('https://', '')}
                        </p>
                      </div>
                      <ArrowUpRight size={14} style={{ marginLeft: 'auto', color: 'var(--text-muted)', flexShrink: 0 }} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;
